import React from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import css from './EditListingDescriptionPanel.css';
import { ensureOwnListing } from '../../util/data';
import { EditListingDescriptionRiderForm, EditListingDescriptionHorseForm } from '../../forms';
import { DESCRIPTION_HORSE, DESCRIPTION_RIDER } from '../EditListingWizard/config';
import {
    commonDisciplines,
    experienceAllowedOptions,
    skills_experience as skillsExperienceOptions,
    skills_general as skillsGeneralOptions,
} from '../../marketplace-custom-config';
import { getUserCountry } from '../../util/location';
import { getTempListingData } from '../../util/localStorage';
import {
    SKILLS_CUSTOM_FIELD_NAME,
    SKILLS_CUSTOM_FIELD_NAME_SECONDARY,
} from '../../forms/EditListingDescriptionForm/EditListingDescriptionPages/EditListingDescriptionPageEight';

const presetLevelFields = commonDisciplines.map(({ id }) => `selectDesiredLevel-${id}`);

const sortSkillsByConfig = (skills, options) => {
    if (!Array.isArray(skills)) return skills;

    const optionOrder = options.map(({ label }) => label);
    return [...skills].sort((first, second) => {
        const firstIndex = optionOrder.indexOf(first);
        const secondIndex = optionOrder.indexOf(second);

        if (firstIndex === -1) return secondIndex === -1 ? 0 : 1;
        if (secondIndex === -1) return -1;
        return firstIndex - secondIndex;
    });
};

const constructSearchSchemaDesiredLevels = ({ publicData }) =>
    publicData.desiredDisciplines.reduce((acc, id) => {
        const { key } =
            experienceAllowedOptions.find(
                ({ key }) => key === publicData[`selectDesiredLevel-${id}`]
            ) || {};

        if (!key) return acc;

        return [...acc, key, `${id}.${key}`];
    }, []);

const EditListingDescriptionPanel = props => {
    const {
        className,
        rootClassName,
        listing,
        onSubmit,
        onChange,
        submitButtonText,
        panelUpdated,
        updateInProgress,
        errors,
        currentTab,
        externalListing,
        ensuredCurrentUser,
        ...rest
    } = props;

    const classes = classNames(rootClassName || css.root, className);
    const currentListing = ensureOwnListing(listing);
    const { description } = currentListing.attributes;
    const {
        requirements,
        ridingsNum,
        availability,
        activities,
        skillsGeneral,
        skillsExperience,
        desiredExperience,
        desiredDisciplines,
        starredDisciplines = [],
        mindestalter,
        openForPartTimeProposals,
        startDateISO,
        endDateISO,
        horseType,
        insurance,
        ...otherFields
    } = currentListing.attributes.publicData;

    const userCountry = getUserCountry();
    const { isOfflineFlow } = rest;

    const tempListingData = getTempListingData();

    const formConfig = {
        [DESCRIPTION_RIDER]: {
            FormTag: EditListingDescriptionRiderForm,
            initialValues: {
                horseType,
                ridingsNum,
                availability,
                activities,
                skillsGeneral,
                skillsExperience,
                desiredExperience,
                desiredDisciplines,
                starredDisciplines,
                mindestalter,
                openForPartTimeProposals,
                startDateBefristet: startDateISO && { date: new Date(startDateISO) },
                endDateBefristet: endDateISO && { date: new Date(endDateISO) },
                insurance,
                ...otherFields,
                ...(isOfflineFlow && tempListingData ? tempListingData : {}),
            },
            handleSubmit: values => {
                const {
                    horseType = null,
                    openForPartTimeProposals = null,
                    startDateBefristet = null,
                    endDateBefristet = null,
                    ridingsNum = null,
                    availability = null,
                    activities = null,
                    desiredExperience = null,
                    desiredDisciplines = [],
                    starredDisciplines = [],
                    skillsGeneral = null,
                    skillsExperience = null,
                    insurance = null,
                    isAutoSave,
                    redirectToListingPage,
                    desiredLevels,
                    requiredQualificationLevel,
                    ...rest
                } = values;

                delete rest[SKILLS_CUSTOM_FIELD_NAME]; // shouldn't be included
                delete rest[SKILLS_CUSTOM_FIELD_NAME_SECONDARY]; // shouldn't be included

                const updateValues = {
                    publicData: {
                        horseType,
                        openForPartTimeProposals,
                        startDateISO:
                            startDateBefristet && new Date(startDateBefristet.date).toISOString(),
                        endDateISO:
                            endDateBefristet && new Date(endDateBefristet.date).toISOString(),
                        ridingsNum,
                        availability,
                        activities,
                        skillsGeneral: sortSkillsByConfig(skillsGeneral, skillsGeneralOptions),
                        skillsExperience: sortSkillsByConfig(
                            skillsExperience,
                            skillsExperienceOptions
                        ),
                        desiredExperience,
                        desiredDisciplines,
                        starredDisciplines,
                        insurance,
                        userCountry,
                        type: 'horse',
                        requiredQualificationLevel,
                        ...rest,
                    },
                };
                /**
                 * Filter those selectDesiredLevel items which are not used anymore
                 */
                const keysToDelete = presetLevelFields.reduce((acc, levelKey) => {
                    const { publicData } = updateValues;
                    const [, discipline] = levelKey.split('-');
                    if (
                        publicData[levelKey] &&
                        !publicData.desiredDisciplines.includes(discipline)
                    ) {
                        return [...acc, levelKey];
                    }
                    return acc;
                }, []);

                if (keysToDelete.length) {
                    keysToDelete.forEach(keyToDelete => {
                        updateValues.publicData[keyToDelete] = null;
                    });
                }
                if (isAutoSave) {
                    updateValues.isAutoSave = isAutoSave;
                }
                if (redirectToListingPage) {
                    updateValues.redirectToListingPage = redirectToListingPage;
                }

                onSubmit({
                    ...updateValues,
                    publicData: {
                        ...updateValues.publicData,
                        /*
                         * SSC_qualification is used for flex-cli search schema
                         */
                        SSC_qualification: requiredQualificationLevel,
                        /**
                         * SSC_desiredDisciplinesLevels is used for flex-cli search schema
                         */
                        SSC_desiredDisciplinesLevels:
                            Array.isArray(desiredDisciplines) && desiredDisciplines.length > 0
                                ? constructSearchSchemaDesiredLevels(updateValues)
                                : null,
                    },
                });
            },
        },
        [DESCRIPTION_HORSE]: {
            FormTag: EditListingDescriptionHorseForm,
            initialValues: { description, requirements },
            handleSubmit: values => {
                const { description, requirements = null } = values;

                const updateValues = {
                    description: description ? description.trim() : null,
                    publicData: {
                        requirements: requirements,
                    },
                };
                onSubmit(updateValues);
            },
        },
    };

    const Form = formConfig[currentTab].FormTag;

    return (
        <div className={classes}>
            <Form
                className={css.form}
                initialValues={{ ...formConfig[currentTab].initialValues }}
                saveActionMsg={submitButtonText}
                onSubmit={formConfig[currentTab].handleSubmit}
                onChange={onChange}
                updated={panelUpdated}
                updateInProgress={updateInProgress}
                fetchErrors={errors}
                externalListing={externalListing}
                currentListing={currentListing}
                ensuredCurrentUser={ensuredCurrentUser}
                {...rest}
            />
        </div>
    );
};

EditListingDescriptionPanel.propTypes = {
    className: string,
    rootClassName: string,

    // We cannot use propTypes.listing since the listing might be a draft.
    listing: object,

    onSubmit: func.isRequired,
    onChange: func.isRequired,
    submitButtonText: string.isRequired,
    panelUpdated: bool.isRequired,
    updateInProgress: bool.isRequired,
    errors: object.isRequired,
    currentTab: string.isRequired,
    externalListing: bool,
};

export default EditListingDescriptionPanel;
